import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { SharedService } from 'src/app/Services/sharedService';
import { MessagingServiceService } from 'src/app/Services/messaging-service.service';
import { isRustAgentUser } from 'src/app/agent-deploy/agent-deploy.config';
import { sysConfig } from 'src/app/sysConfig';

@Component({
  selector: 'app-ss-card',
  standalone: false,
  templateUrl: './ss-card.html',
  styleUrl: './ss-card.css',
})
export class SSCard implements OnChanges, AfterViewInit, OnDestroy {
  @Output() imageOpenerEmitter: EventEmitter<any> = new EventEmitter();
  @Output() deleteScreenShotEmitter: EventEmitter<any> = new EventEmitter();
  @Output() downloadScreenShotEmitter: EventEmitter<any> = new EventEmitter();
  @Input('image') image: any;
  @Input('userList') userList: any = [];
  @Input('isEmployeeProfile') isEmployeeProfile: boolean = false;
  @Input('isAdmin') isAdmin: boolean = false;

  @ViewChild('mediaFrame') mediaFrame?: ElementRef<HTMLElement>;

  readonly sysConfig = sysConfig;

  readonly triggerChipClass: Record<string, string> = {
    'app-changed': 'screenshot-type-chip--appswitch',
    'admin-request': 'screenshot-type-chip--ondemand',
  };

  /** Bound only after near-viewport — avoids many hanging S3 requests freezing the tab. */
  displaySrc: string | null = null;
  imgFailed = false;
  imgLoading = false;
  cachedUser: any = null;
  showBetaBadgeCached = false;

  private observer: IntersectionObserver | null = null;
  private loadTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly loadTimeoutMs = 8000;
  private viewReady = false;

  constructor(
    private router: Router,
    private _sharedService: SharedService,
    private messagingService: MessagingServiceService,
    private host: ElementRef<HTMLElement>
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['image'] || changes['userList']) {
      this.refreshUserCache();
    }
    if (changes['image']) {
      this.resetImageLoad();
      if (this.viewReady) {
        this.ensureObserver();
      }
    }
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.ensureObserver();
  }

  ngOnDestroy(): void {
    this.teardownObserver();
    this.clearLoadTimer();
  }

  private refreshUserCache(): void {
    const userId = this.image?.user_id;
    this.cachedUser = this._sharedService.getUserfromUserList(userId, this.userList);
    if (!sysConfig.rustAgentBetaBadgeEnabled) {
      this.showBetaBadgeCached = false;
      return;
    }
    if (isRustAgentUser(this.cachedUser)) {
      this.showBetaBadgeCached = true;
      return;
    }
    const fbUser = this.messagingService
      .getLatestFirebaseUsersSnapshot()
      .find((user) => user?.id == userId);
    this.showBetaBadgeCached = isRustAgentUser(fbUser);
  }

  private resetImageLoad(): void {
    this.clearLoadTimer();
    this.teardownObserver();
    this.displaySrc = null;
    this.imgFailed = false;
    this.imgLoading = false;
  }

  private ensureObserver(): void {
    if (this.displaySrc || this.imgFailed) {
      return;
    }
    if (typeof IntersectionObserver === 'undefined') {
      this.beginImageLoad();
      return;
    }
    this.teardownObserver();
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          this.beginImageLoad();
          this.teardownObserver();
        }
      },
      { root: null, rootMargin: '160px 0px', threshold: 0.01 }
    );
    const el = this.mediaFrame?.nativeElement || this.host.nativeElement;
    this.observer.observe(el);
  }

  private teardownObserver(): void {
    this.observer?.disconnect();
    this.observer = null;
  }

  private beginImageLoad(): void {
    const url = this.image?.image_url;
    if (!url || this.imgFailed || this.displaySrc) {
      if (!url) {
        this.imgFailed = true;
      }
      return;
    }
    this.imgLoading = true;
    this.displaySrc = url;
    this.clearLoadTimer();
    this.loadTimer = setTimeout(() => {
      // Stuck TCP/S3 hang never fires error — drop the request so the tab stays alive.
      if (this.imgLoading && !this.imgFailed) {
        this.onImgError();
      }
    }, this.loadTimeoutMs);
  }

  private clearLoadTimer(): void {
    if (this.loadTimer) {
      clearTimeout(this.loadTimer);
      this.loadTimer = null;
    }
  }

  onImgLoad(): void {
    this.imgLoading = false;
    this.clearLoadTimer();
  }

  onImgError(): void {
    this.imgLoading = false;
    this.imgFailed = true;
    this.displaySrc = null;
    this.clearLoadTimer();
  }

  openPreviewImage(image: any) {
    if (this.imgFailed || !image?.image_url) {
      return;
    }
    this.imageOpenerEmitter.emit(image);
  }

  deleteScreenShot(image: any) {
    this.deleteScreenShotEmitter.emit(image);
  }

  downloadScreenShot(image: any) {
    this.downloadScreenShotEmitter.emit(image);
  }

  goToUserDetails(userId: number | any) {
    if (this.isEmployeeProfile) {
      return;
    }
    this.router.navigate(['/user-details', userId]);
  }
}
